import { Operator } from "models/Rule";
import type { ModuleScore } from "stores/ApplicationFilters/ApplicationFiltersStore";

/**
 * One condition, paired with its position in the flat filter array.
 *
 * The filter the backend takes is a flat list of conditions, so the index is the only thing that
 * identifies a row: a module can carry several, and two of them can be identical.
 */
export type ModuleScoreConditionRow = {
    entry: ModuleScore;
    index: number;
};

/** Every condition on one module, in the order they appear in the filter. */
export type ModuleScoreGroup = {
    licensedModuleId: string;
    conditions: ModuleScoreConditionRow[];
};

export const MODULE_SCORE_MIN = 0;
export const MODULE_SCORE_MAX = 100;

/**
 * A score range with its ends marked open or closed.
 *
 * Inclusivity is tracked because `>= 70` with `<= 70` selects the score 70 while `>= 70` with
 * `< 70` selects nothing, and telling a user which of those they just built is the whole point.
 */
export type ScoreInterval = {
    min: number;
    minInclusive: boolean;
    max: number;
    maxInclusive: boolean;
};

export const UNBOUNDED_SCORE_INTERVAL: ScoreInterval = {
    min: MODULE_SCORE_MIN,
    minInclusive: true,
    max: MODULE_SCORE_MAX,
    maxInclusive: true,
};

/** The scores one condition admits. A condition with no operator yet admits everything. */
export function toScoreInterval(condition: ModuleScore): ScoreInterval {
    const value = condition.moduleScore;

    switch (condition.operator) {
        case Operator.Gt:
            return { ...UNBOUNDED_SCORE_INTERVAL, min: value, minInclusive: false };
        case Operator.GtEq:
            return { ...UNBOUNDED_SCORE_INTERVAL, min: value, minInclusive: true };
        case Operator.Lt:
            return { ...UNBOUNDED_SCORE_INTERVAL, max: value, maxInclusive: false };
        case Operator.LtEq:
            return { ...UNBOUNDED_SCORE_INTERVAL, max: value, maxInclusive: true };
        case Operator.Eq:
            return { min: value, minInclusive: true, max: value, maxInclusive: true };
        default:
            return UNBOUNDED_SCORE_INTERVAL;
    }
}

export function intersectScoreIntervals(
    left: ScoreInterval,
    right: ScoreInterval,
): ScoreInterval {
    const min = Math.max(left.min, right.min);
    const max = Math.min(left.max, right.max);

    return {
        min,
        // At a shared edge the stricter end wins, so one open end opens the result there.
        minInclusive: [left, right]
            .filter((interval) => interval.min === min)
            .every((interval) => interval.minInclusive),
        max,
        maxInclusive: [left, right]
            .filter((interval) => interval.max === max)
            .every((interval) => interval.maxInclusive),
    };
}

/**
 * The scores a module's whole block admits.
 *
 * Conditions on one module are ANDed into separate EXISTS clauses, so the block admits the overlap
 * of what its conditions admit.
 */
export function getModuleScoreBlockInterval(
    conditions: ModuleScore[],
): ScoreInterval {
    return conditions.reduce(
        (interval, condition) =>
            intersectScoreIntervals(interval, toScoreInterval(condition)),
        UNBOUNDED_SCORE_INTERVAL,
    );
}

export function isScoreIntervalEmpty(interval: ScoreInterval): boolean {
    if (interval.min > interval.max) return true;
    return (
        interval.min === interval.max &&
        !(interval.minInclusive && interval.maxInclusive)
    );
}

/**
 * The condition, if any, that leaves no score satisfying both it and the one at `position`.
 *
 * Conditions are ANDed, so a module's block can only narrow. Someone reaching for `>= 70` and
 * `< 40` is asking to exclude the middle, which is an OR, and what they would actually get is a
 * filter matching nothing. Naming the condition it collides with is what lets the drawer say so.
 *
 * A condition with no operator chosen yet collides with nothing, so a half built row stays quiet
 * until it says something.
 */
export function findConflictingCondition(
    conditions: ModuleScore[],
    position: number,
): ModuleScore | undefined {
    const subject = conditions[position];
    if (!subject || subject.operator === undefined) return undefined;

    return conditions.find((other, otherPosition) => {
        if (otherPosition === position || other.operator === undefined) {
            return false;
        }

        return isScoreIntervalEmpty(
            intersectScoreIntervals(
                toScoreInterval(subject),
                toScoreInterval(other),
            ),
        );
    });
}

/**
 * A module may hold this many conditions at once.
 *
 * Two is what a range needs and what the drawer offers. It is not a technical limit: the filter is
 * a flat list and the backend ANDs however many arrive, so a saved filter carrying more still runs
 * and still reads correctly in the chips.
 */
export const MAX_CONDITIONS_PER_MODULE = 2;

/**
 * The condition dropdown reads in words, because a symbol picked from a list is easy to misread
 * and `>=` next to `<=` is the pair people get wrong. Chips keep the symbols, where the module
 * name and the value give the comparison its context.
 */
export const operatorOptions = [
    {
        label: "Greater than",
        symbol: ">",
        value: Operator.Gt,
    },
    {
        label: "Less than",
        symbol: "<",
        value: Operator.Lt,
    },
    {
        label: "Greater than or equal to",
        symbol: ">=",
        value: Operator.GtEq,
    },
    {
        label: "Less than or equal to",
        symbol: "<=",
        value: Operator.LtEq,
    },
    {
        label: "Equal to",
        symbol: "=",
        value: Operator.Eq,
    },
];

/**
 * Whether a module can take another condition, and what to say when it cannot.
 *
 * Equal to names one exact score. Anything ANDed with it either repeats it or rules it out, so the
 * module is finished once it is chosen.
 */
export function getAddConditionState(conditions: ModuleScore[]): {
    canAdd: boolean;
    reason: string;
} {
    if (conditions.some((condition) => condition.operator === Operator.Eq)) {
        return {
            canAdd: false,
            reason: '"Equal to" is an exact score, so no other condition can be added.',
        };
    }

    if (conditions.length >= MAX_CONDITIONS_PER_MODULE) {
        return {
            canAdd: false,
            reason: `Maximum of ${MAX_CONDITIONS_PER_MODULE} conditions per module.`,
        };
    }

    return {
        canAdd: true,
        reason:
            "Adds another rule to this module's score. Conversations must meet every condition to match.",
    };
}

/**
 * Collects the flat condition list into one group per module, ordered by first appearance.
 *
 * Grouping is a presentation concern only. The filter stays flat on the way to the backend, where
 * each condition becomes its own EXISTS clause and the clauses are ANDed — which is what a grouped
 * block, and a merged chip, say on screen.
 */
export function groupModuleScores(
    moduleScores: ModuleScore[] = [],
): ModuleScoreGroup[] {
    const groups: ModuleScoreGroup[] = [];
    const groupByModuleId = new Map<string, ModuleScoreGroup>();

    moduleScores.forEach((entry, index) => {
        let group = groupByModuleId.get(entry.licensedModuleId);
        if (!group) {
            group = { licensedModuleId: entry.licensedModuleId, conditions: [] };
            groupByModuleId.set(entry.licensedModuleId, group);
            groups.push(group);
        }
        group.conditions.push({ entry, index });
    });

    return groups;
}

/**
 * Phrases a module's conditions as one clause: `>= 80% AND < 90%`.
 *
 * A module's conditions are ANDed, so they read as a single restriction on that module rather than
 * as separate filters that happen to name it twice. A lone condition phrases exactly as it always
 * did, which is what keeps the common case unchanged.
 */
export function formatModuleScoreConditions(conditions: ModuleScore[]): string {
    // Called through an arrow rather than point-free, so map's index argument cannot arrive as
    // useLabel and turn every condition after the first into words.
    return conditions
        .map((condition) => formatModuleScoreCondition(condition))
        .join(" AND ");
}

/** One condition in its compact form: `>= 80%`. */
export function formatModuleScoreCondition(condition: ModuleScore, useLabel: boolean = false): string {
    const option =
        operatorOptions.find((option) => option.value === condition.operator)
             ?? {label: "", symbol: "", value: Operator.Eq};
    return `${useLabel ? option.label : option.symbol} ${condition.moduleScore}%`.trim();
}

/** Says which two conditions collide, in the compact form the chips use. */
export function describeConditionConflict(
    subject: ModuleScore,
    other: ModuleScore,
): string {
    return `No score is both ${formatModuleScoreCondition(subject, true)} and ${formatModuleScoreCondition(other, true)}.`;
}
